import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import SiteHeader from '../components/SiteHeader';
import MasteryBar from '../components/MasteryBar';
import LevelBandPicker from '../components/LevelBandPicker';
import ConfirmDialog from '../components/ConfirmDialog';
import OverflowMenu from '../components/OverflowMenu';
import OpeningCard from '../components/OpeningCard';
import DeepenControl from '../components/DeepenControl';
import { useAuth } from '../auth/AuthContext';
import { studyApi } from '../api/studyApi';
import type { LineLevel, PathStep, RepertoireEntry, StudyStats } from '../../../shared/types';

/**
 * The "today" screen: what is due, how the streak is going, and one obvious
 * button to press. Signed out, the same route is the landing page.
 */
/**
 * One sentence explaining why this step and not another.
 *
 * The path orders by how often you actually reach the material, so the reason
 * is a number it already knows -- saying it out loud is the difference between
 * an instruction and an argument.
 */
function noteFor(step: PathStep | null): string {
  if (!step) return '';
  const reach = `It comes up in about ${step.reachPercent.toFixed(0)}% of your games.`;
  return step.kind === 'deepen'
    ? `Step ${step.number}: take ${step.name} to ${step.level}. ${reach}`
    : `Step ${step.number}: ${step.slotLabel.toLowerCase()}. ${reach}`;
}

export default function HomePage() {
  const { user, getToken, refresh, loading: authLoading } = useAuth();
  const auth = useMemo(() => ({ getToken, refresh }), [getToken, refresh]);

  const [stats, setStats] = useState<StudyStats | null>(null);
  const [repertoire, setRepertoire] = useState<RepertoireEntry[] | null>(null);
  const [error, setError] = useState('');
  // Which card is mid-save, so only that one's control disables.
  const [busyId, setBusyId] = useState<string | null>(null);
  // The entry awaiting confirmation, or null when no dialog is up.
  const [removing, setRemoving] = useState<RepertoireEntry | null>(null);
  // The next suggested opening, so the home screen can answer "what now"
  // without the user having to choose from forty.
  const [nextUp, setNextUp] = useState<PathStep | null>(null);
  // Why the path is suggesting what it is suggesting. An instruction without a
  // reason is one people quietly ignore, and for a 'go deeper' suggestion the
  // reason is the whole argument: this material comes up more often than a new
  // opening would.
  const [focusNote, setFocusNote] = useState('');
  const [adding, setAdding] = useState(false);

  /** Re-read the home screen and the path after either of them has moved. */
  async function refreshSuggestion() {
    const [home, path] = await Promise.all([studyApi.home(auth), studyApi.pathway(auth)]);
    setStats(home.stats);
    setRepertoire(home.repertoire);
    setNextUp(path.nextUp);
    setFocusNote(noteFor(path.nextUp));
  }

  /** Add the suggested opening and advance the suggestion in one click. */
  async function addSuggested() {
    if (!nextUp) return;
    setAdding(true);
    setError('');
    try {
      await studyApi.addToRepertoire(nextUp.openingId, auth);
      // Refresh both: the repertoire gains a card, and the pathway moves on to
      // whatever is next. Doing it from the server rather than guessing keeps
      // the suggestion honest about mastery and ordering.
      await refreshSuggestion();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not add that opening');
    } finally {
      setAdding(false);
    }
  }

  /** Drop an opening from the repertoire. Progress records survive. */
  async function removeOpening(openingId: string, name: string) {
    if (!repertoire) return;
    const before = repertoire;
    setBusyId(openingId);
    setError('');
    // Optimistic: the card disappears immediately, and comes back if the write
    // fails, which is far less jarring than a spinner on a delete.
    setRepertoire((list) => (list ?? []).filter((e) => e.openingId !== openingId));
    try {
      await studyApi.removeFromRepertoire(openingId, auth);
      const h = await studyApi.home(auth);
      setStats(h.stats);
      setRepertoire(h.repertoire);
    } catch (e) {
      setRepertoire(before);
      setError(e instanceof Error ? e.message : `Could not remove ${name}`);
    } finally {
      setBusyId(null);
    }
  }

  /**
   * Change which levels of an opening are scheduled, from the home screen.
   *
   * Optimistic: the entry is patched locally first so the click feels instant,
   * and rolled back if the write fails. The server returns the resolved entry,
   * which replaces the guess -- it is the authority on what a band resolves to.
   */
  async function changeLevels(openingId: string, levels: LineLevel[]) {
    if (!repertoire) return;
    const before = repertoire;
    setBusyId(openingId);
    setError('');
    try {
      const updated = await studyApi.setRepertoireSelection(openingId, { levels }, auth);
      setRepertoire((list) =>
        (list ?? []).map((e) => (e.openingId === openingId ? updated : e)),
      );
      // Due and new counts move with the selection, so refresh the headline
      // figures rather than leaving them contradicting the card below them.
      studyApi.home(auth).then((h) => setStats(h.stats)).catch(() => {});
    } catch (e) {
      setRepertoire(before);
      setError(e instanceof Error ? e.message : 'Could not change your levels');
    } finally {
      setBusyId(null);
    }
  }

  useEffect(() => {
    if (authLoading || !user) return;
    let cancelled = false;
    studyApi
      .pathway(auth)
      .then((p) => {
        setNextUp(p.nextUp);
        setFocusNote(noteFor(p.nextUp));
      })
      .catch(() => setNextUp(null));
    // One request for both halves — see studyApi.home.
    studyApi
      .home(auth)
      .then((home) => {
        if (cancelled) return;
        setStats(home.stats);
        setRepertoire(home.repertoire);
      })
      .catch((e) => !cancelled && setError(e instanceof Error ? e.message : 'Failed to load your progress'));
    return () => {
      cancelled = true;
    };
  }, [auth, user, authLoading]);

  if (!authLoading && !user) return <Landing />;

  const toStudy = (stats?.dueNow ?? 0) + Math.min(stats?.newAvailable ?? 0, 5);

  return (
    <>
      <SiteHeader />
      <main className="page page--wide">
        <header className="home-hero">
          <div>
            <h1 className="home-title">
              {greeting()}{user ? `, ${user.displayName}` : ''}
            </h1>
            <p className="muted">
              {toStudy > 0
                ? `${stats?.dueNow ?? 0} due for review${(stats?.newAvailable ?? 0) > 0 ? `, ${stats?.newAvailable} new to learn` : ''}.`
                : repertoire && repertoire.length === 0
                  ? 'Pick an opening to start building your repertoire.'
                  : 'Nothing due right now — everything is on schedule.'}
            </p>
          </div>
          {/* Two doors, because they are different activities. The plain /study
              link lets each line choose its own mode, so a session mixes
              reading and testing -- fine as a default, useless when you sat
              down specifically to be quizzed. Forcing mode=practice keeps every
              line in Practice from first to last. */}
          <div className="home-hero-actions">
            <Link className="btn btn--primary btn--large" to="/study">
              {toStudy > 0 ? `Study ${toStudy} ${toStudy === 1 ? 'line' : 'lines'}` : 'Study anyway'}
            </Link>
            <Link className="btn btn--large" to="/study?mode=practice">
              {toStudy > 0 ? `Practice ${toStudy}` : 'Practice anyway'}
            </Link>
          </div>
        </header>

        {error && <p className="error">{error}</p>}

        <div className="stat-row">
          <Stat
            value={stats?.currentStreak ?? 0}
            label="day streak"
            hint={stats?.longestStreak ? `best ${stats.longestStreak}` : undefined}
          />
          {/* Due now is the one number you act on, so the tile is the action. */}
          <Stat
            value={stats?.dueNow ?? 0}
            label="due now"
            to={(stats?.dueNow ?? 0) > 0 ? '/study?mode=practice' : undefined}
            hint={(stats?.dueNow ?? 0) > 0 ? 'practice them' : undefined}
          />
          <Stat
            value={stats?.newAvailable ?? 0}
            label="new to learn"
            to={(stats?.newAvailable ?? 0) > 0 ? '/study?mode=study' : undefined}
            hint={(stats?.newAvailable ?? 0) > 0 ? 'study them' : undefined}
          />
          <Stat value={stats?.reviewsToday ?? 0} label="reviews today" />
        </div>

        <section>
          <div className="section-head">
            <h2>Your repertoire</h2>
            <Link to="/openings" className="section-link">
              Add an opening →
            </Link>
          </div>

          {!repertoire ? (
            <p className="muted">Loading…</p>
          ) : repertoire.length === 0 ? (
            <div className="empty-card">
              <p>
                Your repertoire is empty. Browse the openings, read the theory behind one you like,
                and add it — its lines will start appearing in your daily reviews.
              </p>
              <Link className="btn btn--primary" to="/openings">
                Browse openings
              </Link>
            </div>
          ) : (
            <ul className="rep-list">
              {repertoire.map((entry) => (
                <li key={entry.openingId}>
                  {/* The same tile as the browse page and the pathway. It used
                      to be a bespoke card here, which is why an opening looked
                      like a different thing depending on the page. */}
                  <OpeningCard
                    opening={{ ...entry, id: entry.openingId }}
                    corner={
                      <OverflowMenu label={`Actions for ${entry.name}`}>
                        {(close) => (
                          <>
                            <Link className="overflow-item" to={`/openings/${entry.openingId}`}>
                              Open the theory
                            </Link>
                            <button
                              type="button"
                              className="overflow-item overflow-item--danger"
                              disabled={busyId === entry.openingId}
                              onClick={() => {
                                close();
                                setRemoving(entry);
                              }}
                            >
                              Remove from repertoire
                            </button>
                          </>
                        )}
                      </OverflowMenu>
                    }
                    badge={
                      entry.dueCount > 0 ? (
                        <span className="pill pill--due">{entry.dueCount} due</span>
                      ) : entry.newCount > 0 ? (
                        <span className="pill pill--new">{entry.newCount} new</span>
                      ) : (
                        <span className="pill pill--done">up to date</span>
                      )
                    }
                    footer={
                      <>
                        <MasteryBar value={entry.mastery} compact />
                        <LevelBandPicker
                          counts={entry.levelCounts}
                          selectedByLevel={entry.selectedByLevel}
                          onChange={(levels) => changeLevels(entry.openingId, levels)}
                          disabled={busyId === entry.openingId}
                          compact
                        />
                        <div className="rep-actions">
                          <Link
                            className="btn btn--small"
                            to={`/study?opening=${entry.openingId}&mode=study`}
                          >
                            Study
                          </Link>
                          <Link
                            className="btn btn--small btn--primary"
                            to={`/study?opening=${entry.openingId}&mode=practice`}
                          >
                            Practice
                          </Link>
                        </div>
                      </>
                    }
                  />
                </li>
              ))}
            </ul>
          )}
        </section>

        {/* What to learn next, so the answer is never "choose from forty". */}
        {/* The same card the browse page shows, so a suggestion is never
            thinner than the thing it suggests. */}
        {nextUp && (
          <section className="home-next">
            <span className="home-next-label">
              {nextUp.kind === 'deepen' ? 'Ready for more · ' : 'Suggested next · '}
              {nextUp.slotLabel}
            </span>
            {focusNote && <p className="home-next-why muted">{focusNote}</p>}
            <OpeningCard
              opening={{ ...nextUp, id: nextUp.openingId }}
              footer={
                <div className="home-next-actions">
                  {/* The suggestion is now two different things depending on
                      where you are: a new opening while the repertoire has a
                      gap, and another level of one you already play once it
                      does not. Offering "Add to my repertoire" for an opening
                      that is already in it was the giveaway that the home
                      screen only understood half the path. */}
                  {nextUp.kind === 'deepen' && nextUp.depth ? (
                    <DeepenControl
                      openingId={nextUp.openingId}
                      name={nextUp.name}
                      depth={nextUp.depth}
                      auth={auth}
                      onChanged={refreshSuggestion}
                    />
                  ) : (
                    <button
                      type="button"
                      className="btn btn--primary"
                      disabled={adding}
                      onClick={addSuggested}
                    >
                      {adding ? 'Adding…' : 'Add to my repertoire'}
                    </button>
                  )}
                </div>
              }
            />
          </section>
        )}
      </main>

      {removing && (
        <ConfirmDialog
          title={`Remove ${removing.name}?`}
          confirmLabel="Remove from repertoire"
          onCancel={() => setRemoving(null)}
          onConfirm={() => {
            const entry = removing;
            setRemoving(null);
            removeOpening(entry.openingId, entry.name);
          }}
          body={
            <>
              <p>
                It will stop appearing in your reviews and its {removing.lineCount} scheduled{' '}
                {removing.lineCount === 1 ? 'line' : 'lines'} will leave your queue.
              </p>
              {/* The honest detail: removal deletes the repertoire entry, not
                  the progress records, so this really is reversible. Saying so
                  turns a frightening decision into an easy one. */}
              <p className="muted">
                Your progress is kept. Add it back any time and you will pick up exactly where you
                left off, not start over.
              </p>
            </>
          }
        />
      )}
    </>
  );
}

function greeting(): string {
  const hour = new Date().getHours();
  if (hour < 12) return 'Good morning';
  if (hour < 18) return 'Good afternoon';
  return 'Good evening';
}

/**
 * A number on the home screen. Given a `to`, the whole tile becomes the link —
 * the counts people want to act on ("12 due now") should not make you go and
 * find a separate button.
 */
function Stat({
  value,
  label,
  hint,
  to,
}: {
  value: number;
  label: string;
  hint?: string;
  to?: string;
}) {
  const body = (
    <>
      <span className="stat-value">{value}</span>
      <span className="stat-label">{label}</span>
      {hint && <span className="stat-hint">{hint}</span>}
    </>
  );
  if (!to) return <div className="stat">{body}</div>;
  return (
    <Link to={to} className="stat stat--action">
      {body}
    </Link>
  );
}

/** The signed-out landing page. */
function Landing() {
  return (
    <>
      <SiteHeader />
      <main className="page page--wide">
        <section className="landing">
          <h1 className="landing-title">
            Learn openings by understanding them,
            <br />
            not by memorizing move lists.
          </h1>
          <p className="landing-lead">
            Forty openings, each with the strategy written out in full — the pawn structure, the
            plans for both sides, the squares the game turns on — and every single move you have to
            find comes with the reason it is played. Then spaced repetition makes sure it sticks.
          </p>
          <div className="landing-actions">
            <Link className="btn btn--primary btn--large" to="/openings">
              Browse the openings
            </Link>
            <Link className="btn btn--large" to="/register">
              Create an account
            </Link>
          </div>
          <div className="landing-points">
            <Point title="Every move explained">
              Not “this is the book move” — what it achieves, what it stops, and what happens if you
              play the natural-looking alternative instead.
            </Point>
            <Point title="Theory you can actually use">
              A written strategic guide for each opening: the structure, the plans, the key squares
              and the mistakes people make. Read it once and the moves start to make sense.
            </Point>
            <Point title="Reviews timed to catch you">
              Each line is scheduled to come back just before you would forget it, so twenty minutes
              a day is enough to hold a whole repertoire in your head.
            </Point>
          </div>
        </section>
      </main>
    </>
  );
}

function Point({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="landing-point">
      <h3>{title}</h3>
      <p>{children}</p>
    </div>
  );
}

